import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { StackBillingBreakdown } from "@domains/application/types";
import { Application, StackStatus } from "src/domains/application/types";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { formatCloudProvider } from "src/shell/styles/themeUtils";

import type { StackMetrics } from "../types";

// ── Types ────────────────────────────────────────────────────────────────────

interface EnvironmentInfo {
  slug: string;
  name: string;
}

interface AppResource {
  alias: string;
  resource: {
    alias: string;
    name: string;
    status: string;
    resourceType: string;
  };
}

interface DeploymentEventRow {
  systemAlias: string;
  step: string;
  status: string;
  severity: string;
  message: string;
  createdAt: string;
}

interface SummaryTabProps {
  system: Application;
  metrics: StackMetrics | null;
  metricsLoading: boolean;
  onTabChange?: (tab: string) => void;
  environments?: EnvironmentInfo[];
  onPromote?: (slug: string) => void;
  billingData?: StackBillingBreakdown | null;
  orgAlias?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function severityDotColor(severity: string): string {
  const s = (severity ?? "").toLowerCase();
  if (s === "error") return "#f43f5e";
  if (s === "warning") return "#f59e0b";
  if (s === "info") return "#3b82f6";
  return "#10b981";
}

const PIPELINE_MODE_LABELS: Record<string, string> = {
  rusha_managed: "Rusha managed",
  ci_build_rusha_deploy: "CI build, Rusha deploy",
};

// ── Card wrapper matching mockup CSS ─────────────────────────────────────────

function MockupCard({
  title,
  action,
  children,
  danger,
  mb = 2.5,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  danger?: boolean;
  mb?: number;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: `1px solid ${danger ? theme.palette.error.main : alpha(theme.palette.divider, 0.08)}`,
        borderRadius: "12px",
        p: 2.5,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        mb,
        ...(danger && { bgcolor: alpha(theme.palette.error.main, 0.04) }),
      }}
    >
      {(title || action) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2, gap: 1.5 }}
        >
          {title && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "text.secondary",
              }}
            >
              {title}
            </Typography>
          )}
          {action}
        </Stack>
      )}
      {children}
    </Box>
  );
}

// ── Badge component ──────────────────────────────────────────────────────────

function Badge({
  icon,
  label,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  color: "success" | "error" | "warning" | "info" | "default";
}) {
  const colorMap = {
    success: "#10b981",
    error: "#f43f5e",
    warning: "#f59e0b",
    info: "#3b82f6",
    default: "#94a3b8",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        py: 0.625,
        px: 1.25,
        borderRadius: "6px",
        border: `1px solid ${alpha("#94a3b8", 0.2)}`,
        fontSize: 12,
        color: "text.secondary",
        bgcolor: "background.paper",
      }}
    >
      <Box component="span" sx={{ fontSize: 14, color: colorMap[color] }}>
        {icon}
      </Box>
      {label}
    </Box>
  );
}

// ── Meta row ─────────────────────────────────────────────────────────────────

function MetaRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        borderBottom: `1px solid ${alpha("#94a3b8", 0.12)}`,
        fontSize: 13,
      }}
    >
      <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 13, color: "text.primary" }}>{children}</Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SummaryTab({
  system,
  metrics: _metrics,
  metricsLoading: _metricsLoading,
  onTabChange,
  environments = [],
  onPromote,
  billingData,
}: SummaryTabProps): React.ReactElement {
  const theme = useTheme();

  // ── Fetch resources + recent activity ──────────────────────────────────────
  const [resources, setResources] = useState<AppResource[]>([]);
  const [recentEvents, setRecentEvents] = useState<DeploymentEventRow[]>([]);

  const fetchOverviewData = useCallback(async () => {
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${encodeURIComponent(system.alias)}/resources`,
        { credentials: "include" },
      );
      if (res.ok) setResources((await res.json()) as AppResource[]);
    } catch {
      // best-effort
    }

    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/deployment-events?limit=4`,
        { credentials: "include" },
      );
      if (res.ok) {
        const data = (await res.json()) as { events: DeploymentEventRow[] };
        setRecentEvents(data.events ?? []);
      }
    } catch {
      // best-effort
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchOverviewData();
  }, [fetchOverviewData]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isDeploying = ["deploying", "building", "provisioning"].includes(
    (system.status ?? "").toLowerCase(),
  );
  const isFailed = ["failed", "error", "provisioning_failed"].includes(
    (system.status ?? "").toLowerCase(),
  );
  const isRunning = ["running", "active"].includes(
    (system.status ?? "").toLowerCase(),
  );
  const mtdCost = billingData?.totalUsd ? `$${billingData.totalUsd}` : "$0.00";

  return (
    <Grid container spacing={3}>
      {/* Left column (2fr) */}
      <Grid item xs={12} md={8}>
        {/* About card */}
        <MockupCard title="About">
          {/* Badge row */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Badge
              icon={isFailed ? "✕" : "✓"}
              label={isFailed ? "Build failing" : "Build passing"}
              color={isFailed ? "error" : "success"}
            />
            <Badge
              icon={isDeploying ? "⚡" : "✓"}
              label={
                isDeploying
                  ? "Deploy in progress"
                  : isRunning
                    ? "Deployed"
                    : "Not deployed"
              }
              color={isDeploying ? "info" : isRunning ? "success" : "default"}
            />
            <Badge icon="$" label={`${mtdCost} MTD`} color="default" />
          </Stack>

          {/* Readme area */}
          <Box
            sx={{
              bgcolor: alpha("#0a1424", 0.4),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              borderRadius: "8px",
              p: 2.5,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>
              {system.name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1 }}>
              {system.description || "No description available."}
            </Typography>
            {system.vcsRepoUrl && (
              <Typography
                sx={{ fontSize: 14, color: "text.secondary", mb: 0.5 }}
              >
                <strong>Source:</strong>{" "}
                <Link
                  href={system.vcsRepoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "primary.main" }}
                >
                  {system.vcsRepoUrl.replace(/^https?:\/\/github\.com\//, "")}
                </Link>
              </Typography>
            )}
            {system.deployedAt && (
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                <strong>Last deploy:</strong> {relativeTime(system.deployedAt)}
              </Typography>
            )}
          </Box>
        </MockupCard>

        {/* Environments card */}
        <MockupCard
          title="Environments"
          action={
            <Button
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                color: "primary.main",
              }}
              onClick={() => onTabChange?.("configuration")}
            >
              Manage
            </Button>
          }
        >
          <Grid container spacing={1.5}>
            {environments.length === 0 && (
              <Grid item xs={12}>
                <Typography
                  sx={{ fontSize: 13, color: "text.secondary", py: 1 }}
                >
                  No environments configured.
                </Typography>
              </Grid>
            )}
            {environments.map((env) => {
              const isCurrent =
                system.status === StackStatus.RUNNING ||
                system.status === StackStatus.DEPLOYING;
              return (
                <Grid item xs={12} sm={4} key={env.slug}>
                  <Box
                    sx={{
                      p: 1.75,
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      borderRadius: "8px",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "text.primary",
                        }}
                      >
                        {env.name}
                      </Typography>
                      <Chip
                        label={
                          isCurrent && env.slug === "dev"
                            ? "Running"
                            : "Not deployed"
                        }
                        size="small"
                        color={
                          isCurrent && env.slug === "dev"
                            ? "success"
                            : "default"
                        }
                        variant={
                          isCurrent && env.slug === "dev"
                            ? "filled"
                            : "outlined"
                        }
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {system.deployedAt && env.slug === "dev"
                        ? `Last deploy: ${relativeTime(system.deployedAt)}`
                        : "Ready to promote"}
                    </Typography>
                    {env.slug !== "dev" && onPromote && (
                      <Typography
                        component="button"
                        onClick={() => onPromote(env.slug)}
                        sx={{
                          fontSize: 12,
                          color: "primary.main",
                          cursor: "pointer",
                          mt: 1.25,
                          fontWeight: 600,
                          background: "none",
                          border: "none",
                          padding: 0,
                        }}
                      >
                        Promote →
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </MockupCard>
      </Grid>

      {/* Right column (1fr) */}
      <Grid item xs={12} md={4}>
        {/* Details card */}
        <MockupCard title="Details">
          <MetaRow label="Source">
            {system.vcsRepoUrl ? (
              <Link
                href={system.vcsRepoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", fontSize: 13 }}
              >
                {system.vcsRepoUrl.replace(/^https?:\/\/github\.com\//, "")}
              </Link>
            ) : (
              "—"
            )}
          </MetaRow>
          <MetaRow label="Deploy mode">
            {PIPELINE_MODE_LABELS[system.pipelineMode ?? ""] ?? "Rusha managed"}
          </MetaRow>
          <MetaRow label="Region">{system.region ?? "—"}</MetaRow>
          <MetaRow label="Cloud Provider">
            {formatCloudProvider(system.cloudProvider)}
          </MetaRow>
          <MetaRow label="Created">
            {system.createdAt
              ? new Date(system.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "—"}
          </MetaRow>
        </MockupCard>

        {/* Resources card */}
        {resources.length > 0 && (
          <MockupCard title="Resources">
            {resources.map((ar) => (
              <MetaRow key={ar.alias} label={ar.resource.alias}>
                <Chip
                  label={ar.resource.status}
                  size="small"
                  color={
                    ar.resource.status === "running" ? "success" : "default"
                  }
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </MetaRow>
            ))}
          </MockupCard>
        )}

        {/* Latest Activity card */}
        <MockupCard
          title="Latest Activity"
          action={
            <Button
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                color: "primary.main",
              }}
              onClick={() => onTabChange?.("events")}
            >
              View all
            </Button>
          }
        >
          {recentEvents.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary", py: 1 }}>
              No recent activity.
            </Typography>
          ) : (
            recentEvents.slice(0, 4).map((event, _i) => (
              <Stack
                key={_i}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  py: 1.25,
                  borderBottom:
                    _i < Math.min(recentEvents.length, 4) - 1
                      ? `1px solid ${alpha(theme.palette.divider, 0.08)}`
                      : "none",
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: severityDotColor(event.severity),
                    mt: 0.75,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                    {event.message || event.step}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {relativeTime(event.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            ))
          )}
        </MockupCard>
      </Grid>
    </Grid>
  );
}

export default SummaryTab;
